
import React, { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, X } from 'lucide-react';
import { useLanguage } from '@/contexts/language';
import { chiapas_cities } from '@/data/chiapas/cities-data';
import { hidalgo_cities } from '@/data/hidalgo/cities-data';
import CityHoverCard from '@/components/cities/CityHoverCard';

interface SpecialRegionsSearchProps {
  onCitySelect: (cityId: string) => void;
  activeRegion?: 'CHIS' | 'HGO' | null;
}

const SpecialRegionsSearch: React.FC<SpecialRegionsSearchProps> = ({ 
  onCitySelect,
  activeRegion 
}) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<string>(activeRegion === 'HGO' ? 'hidalgo' : 'chiapas');
  const [chiapasSearch, setChiapasSearch] = useState('');
  const [hidalgoSearch, setHidalgoSearch] = useState('');
  
  // Filter cities based on search term
  const filteredChiapasCities = useMemo(() => {
    const searchTerm = chiapasSearch.toLowerCase();
    if (!searchTerm) return chiapas_cities;
    
    return chiapas_cities.filter(city => 
      city.name.toLowerCase().includes(searchTerm)
    );
  }, [chiapasSearch]);
  
  const filteredHidalgoCities = useMemo(() => {
    const searchTerm = hidalgoSearch.toLowerCase();
    if (!searchTerm) return hidalgo_cities;
    
    return hidalgo_cities.filter(city => 
      city.name.toLowerCase().includes(searchTerm)
    );
  }, [hidalgoSearch]);
  
  // Handle clear search
  const clearChiapasSearch = () => setChiapasSearch('');
  const clearHidalgoSearch = () => setHidalgoSearch('');
  
  // Update tab when active region changes
  React.useEffect(() => {
    if (activeRegion === 'CHIS') {
      setActiveTab('chiapas');
    } else if (activeRegion === 'HGO') {
      setActiveTab('hidalgo');
    }
  }, [activeRegion]);
  
  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="grid grid-cols-2 mb-2">
        <TabsTrigger value="chiapas" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-900">
          Chiapas
        </TabsTrigger>
        <TabsTrigger value="hidalgo" className="data-[state=active]:bg-orange-100 data-[state=active]:text-orange-900">
          Hidalgo
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="chiapas" className="border-t pt-2">
        <div className="space-y-4">
          {/* Chiapas Search */}
          <div className="relative">
            <Input
              type="text"
              placeholder={t('searchChiapasPlaceholder')}
              value={chiapasSearch}
              onChange={(e) => setChiapasSearch(e.target.value)}
              className="pl-9 pr-9"
            />
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            {chiapasSearch && (
              <button 
                onClick={clearChiapasSearch}
                className="absolute right-3 top-2.5"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
          </div>
          
          {/* Chiapas Cities List */}
          <div className="bg-purple-50/50 rounded-md border border-purple-100 overflow-hidden">
            <ScrollArea className="h-48 rounded-md">
              <div className="p-2">
                {filteredChiapasCities.length > 0 ? (
                  <div className="space-y-1">
                    {filteredChiapasCities.map(city => (
                      <CityHoverCard 
                        key={city.id || city.city_id} 
                        city={city}
                        onCitySelect={onCitySelect}
                      >
                        <div className="flex items-center p-2 rounded-md hover:bg-purple-100 cursor-pointer">
                          <div className={`h-2 w-2 rounded-full mr-2 ${
                            city.church_presence === 'High' ? 'bg-green-500' :
                            city.church_presence === 'Medium' ? 'bg-amber-500' :
                            city.church_presence === 'Low' ? 'bg-orange-500' :
                            'bg-red-500'
                          }`} />
                          <span className="text-sm">{city.name}</span>
                        </div>
                      </CityHoverCard>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-sm text-gray-500">
                    {t('noResults')}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </TabsContent>
      
      <TabsContent value="hidalgo" className="border-t pt-2">
        <div className="space-y-4">
          {/* Hidalgo Search */}
          <div className="relative">
            <Input
              type="text"
              placeholder={t('searchHidalgoPlaceholder')}
              value={hidalgoSearch}
              onChange={(e) => setHidalgoSearch(e.target.value)}
              className="pl-9 pr-9"
            />
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            {hidalgoSearch && (
              <button 
                onClick={clearHidalgoSearch}
                className="absolute right-3 top-2.5"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
          </div>
          
          {/* Hidalgo Cities List */}
          <div className="bg-orange-50/50 rounded-md border border-orange-100 overflow-hidden">
            <ScrollArea className="h-48 rounded-md">
              <div className="p-2">
                {filteredHidalgoCities.length > 0 ? (
                  <div className="space-y-1">
                    {filteredHidalgoCities.map(city => (
                      <CityHoverCard 
                        key={city.id || city.city_id} 
                        city={city}
                        onCitySelect={onCitySelect}
                      >
                        <div className="flex items-center p-2 rounded-md hover:bg-orange-100 cursor-pointer">
                          <div className={`h-2 w-2 rounded-full mr-2 ${
                            city.church_presence === 'High' ? 'bg-green-500' :
                            city.church_presence === 'Medium' ? 'bg-amber-500' :
                            city.church_presence === 'Low' ? 'bg-orange-500' :
                            'bg-red-500'
                          }`} />
                          <span className="text-sm">{city.name}</span>
                        </div>
                      </CityHoverCard>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-sm text-gray-500">
                    {t('noResults')}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
};

export default SpecialRegionsSearch;
